// ===============================
// src/Pages/Admin/agenda/DayAgenda.jsx
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../firebase/config";
import { doc, onSnapshot } from "firebase/firestore";
import { Clock } from "lucide-react";
import { getReglaServicio } from "../../Usuario/Agenda/config/reglasServicios";


/* =============================
   Helpers de fecha/hora
============================= */
const fmt = (n) => String(n).padStart(2, "0");

const toDate = (ymd, hhmm) => {
  const [y, m, d] = ymd.split("-").map(Number);
  const [h, min] = hhmm.split(":").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, h || 0, min || 0);
};

const toHHmm = (d) => `${fmt(d.getHours())}:${fmt(d.getMinutes())}`;

const addMin = (d, m) => new Date(d.getTime() + m * 60000);

const hhmmToMin = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Para convertir HH:mm a Date considerando "wrap" (si el hh está antes del inicio visual, es del día siguiente)
const toDateInView = (ymd, hhmm, viewStartHHmm) => {
  let d = toDate(ymd, hhmm);
  if (hhmmToMin(hhmm) < hhmmToMin(viewStartHHmm)) d = addMin(d, 24 * 60);
  return d;
};


const overlaps = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;

const normModalidad = (m) => (m || "").toString().toLowerCase().trim();

const norm = (x) => (x ?? "").toString();

const getReglaFor = (r) => {
  const cat =
    norm(r.servicio_categoria) ||
    norm(r.servicioCategoria) ||
    norm(r.categoria) ||
    norm(r.servicio_raw); // fallback

  const varnt =
    norm(r.servicio_variante) ||
    norm(r.servicioVariante) ||
    norm(r.variante) ||
    norm(r.servicio_raw); // fallback

  const mod = norm(r.modalidad);
  const esPrimera = !!r.esPrimeraSesion;

  // Firma correcta: (categoria, variante, modalidad, esPrimeraSesion)
  return getReglaServicio(cat, varnt, mod, esPrimera);
};

const isGrupalReserva = (r) => {
  const regla = getReglaFor(r) || {};

  // 1) ✅ Prioridad absoluta: regla del servicio
  if (regla?.permiteTopar === true) return true;
  if (regla?.permiteTopar === false) return false;

  // 2) Fallback SOLO si la regla no matcheó
  if (r?.permiteTopar === true) return true;
  if (normModalidad(r?.modalidad) === "grupal") return true;

  return false;
};


const isExclusiva = (r) => {
  // 1) si viene explícito en la reserva (tu doc tiene permiteTopar:false y tipoSesion:"exclusiva")
  if (r?.permiteTopar === false) return true;
  if ((r?.tipoSesion || "").toLowerCase().includes("exclu")) return true;

  // 2) si la regla dice que es exclusiva
  const regla = getReglaFor(r);
  if (regla?.permiteTopar === false) return true;

  // 3) fallback por texto (por si el mapeo falla)
  const raw = `${norm(r.servicio_categoria)} ${norm(r.servicio_variante)} ${norm(r.servicio_raw)} ${norm(r.modalidad)}`.toLowerCase();
  return (
    raw.includes("kine") ||
    raw.includes("recovery") ||
    raw.includes("masaje") ||
    raw.includes("masoterapia") ||
    raw.includes("pareja") ||
    raw.includes("primera")
  );
};

const getPreBufferMin = (_date, r) => {
  const direct = Number(r.bufferPrevioMin ?? 0);

  const regla = getReglaFor(r);
  const fromRule = Number(regla?.bloqueaPrevio ?? 0);

  // si la regla está bien, esto ya te da 30
  const base = Math.max(direct, fromRule);

  // si por alguna razón la regla no matcheó, igual aplica 30 a exclusivas
  if (base === 0 && isExclusiva(r)) return 30;

  return base;
};

const getPostBufferMin = (_date, _r) => 0; // 👈 ya no usamos buffer post


const getDuracionMin = (r) =>
  Number(r.duracionMin ?? r.durationMin ?? getReglaFor(r)?.duracionMin ?? 60);

const getWindows = (date, r) => {
  const start = toDate(date, r.hora);
  const end = addMin(start, getDuracionMin(r));

  const regla = getReglaFor(r) || {};
  const esGrupal = isGrupalReserva(r);

  // ✅ grupales NO tienen buffer previo
  const pre = esGrupal ? 0 : getPreBufferMin(date, r);

  const preStart = addMin(start, -pre);
  const preEnd = start;

  return {
    start,
    end,
    preStart,
    preEnd,

    // post eliminado, mantenemos por compatibilidad
    postStart: end,
    postEnd: end,

    // ✅ bloqueo total: solo previo (si existe) + sesión
    blockStart: preStart,
    blockEnd: end,
  };
};




/* =============================
   Generación dinámica de slots
============================= */
const buildSlots30 = (ymd, start = "07:30", end = "20:00") => {
  const res = [];
  let cur = toDate(ymd, start);
  const endD = toDate(ymd, end);

  while (cur <= endD) {
    res.push(toHHmm(cur));
    cur = addMin(cur, 30); // 👈 MEDIA HORA
  }
  return res;
};

/* =============================
   Estado visual
============================= */
const EstadoPill = ({ estado }) => {
  const map = {
    pendiente: "bg-amber-100 text-amber-800",
    aprobada: "bg-emerald-100 text-emerald-800",
    realizada: "bg-sky-100 text-sky-800",
    realizada_asiste: "bg-sky-100 text-sky-800",
    realizada_no_asiste: "bg-rose-100 text-rose-800",
    cancelada: "bg-gray-200 text-gray-700",
    rechazada: "bg-rose-200 text-rose-700",
    tentativa_break: "bg-purple-200 text-purple-800",
  };

  const label =
    estado === "pendiente"
      ? "Pendiente"
      : estado === "aprobada"
      ? "Reservada"
      : estado === "realizada" || estado === "realizada_asiste"
      ? "Realizada"
      : estado === "realizada_no_asiste"
      ? "Realizada (No asistió)"
      : estado === "rechazada"
      ? "Rechazada"
      : estado === "cancelada"
      ? "Cancelada"
      : estado === "tentativa_break"
      ? "Tentativa"
      : "—";

  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
        map[estado] || "bg-slate-100 text-slate-700"
      }`}
    >
      {label}
    </span>
  );
};

/* =============================
   COMPONENTE PRINCIPAL
============================= */
export default function DayAgenda({ date, reservations, breaks, onSlotClick }) {
  const [multiChoice, setMultiChoice] = useState(null);
  const [vistaTecnica, setVistaTecnica] = useState(false);

  // =============================
// Responsive: detecta mobile (Tailwind sm = 640px)
// =============================
const [isMobile, setIsMobile] = useState(false);

useEffect(() => {
  const mq = window.matchMedia("(max-width: 639px)");
  const onChange = () => setIsMobile(mq.matches);
  onChange();
  mq.addEventListener?.("change", onChange);
  return () => mq.removeEventListener?.("change", onChange);
}, []);


  /* =============================
     1) Leer horarioBase dinámico
  ============================== */
  const [horarioBase, setHorarioBase] = useState({
    start: "07:30",
    end: "20:00",
  });

  useEffect(() => {
    const ref = doc(db, "breaks_weekly", "default");
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data();
      if (data?.horarioBase) {
        setHorarioBase({
          start: data.horarioBase.start || "07:30",
          end: data.horarioBase.end || "20:00",
        });
      }
    });
    return () => unsub();
  }, []);

    // Permite abrir una reserva manual sin restricciones de horario
  const abrirReservaManualLibre = () => {
    onSlotClick?.({
      status: "manual",
      dateISO: date,
      start: horarioBase.start || "07:30",
      end: horarioBase.start || "07:30",
      reservasEnBloque: [],
      isBreak: false,
    });
  };

  /* =============================
     2) Crear slots de 30 minutos
  ============================== */
  const slots = useMemo(() => {
    return buildSlots30(date, horarioBase.start, horarioBase.end);
  }, [date, horarioBase]);

  /* =============================
     3) Construir mapa de reservas 
        → ocupan varios slots
  ============================== */
  const bySlot = useMemo(() => {
    const map = new Map();

    reservations.forEach((r) => {
      if (!r.hora) return;

      const duration = r.durationMin || 60; // 1 hora por defecto
      const startD = toDate(date, r.hora);
      const endD = addMin(startD, duration);

      slots.forEach((slot) => {
        const s = toDate(date, slot);
        const e = addMin(s, 30);

        if (overlaps(s, e, startD, endD)) {
          map.set(slot, [...(map.get(slot) || []), r]);
        }
      });
    });

    return map;
  }, [reservations, slots, date]);

    // =============================
  // 2.5) Ocupación por slot (30m)
  //  - marca: bufferPrevio, sesionReal, bufferPost
  //  - marca: topables/capacidad para sesiones grupales
  // =============================
  const slotInfo = useMemo(() => {
    const STEP = 30;
    const m = new Map();

    (reservations || []).forEach((r) => {
      if (!r?.hora) return;

      const regla = getReglaFor(r) || {};
      const dur = getDuracionMin(r);

      const start = toDate(date, r.hora);
      const end = addMin(start, dur);

      const esGrupal =
       regla?.permiteTopar === true || r?.permiteTopar === true || normModalidad(r?.modalidad) === "grupal";
      const cap = Number(regla?.capacidadMax ?? 2);

      const pre = esGrupal ? 0 : getPreBufferMin(date, r);

      // 👇 solo prebuffer (post eliminado)
      const preStart = addMin(start, -pre);
      const postEnd = end;

      for (let cur = new Date(preStart); cur < postEnd; cur = addMin(cur, STEP)) {

        const hh = toHHmm(cur);

        if (!m.has(hh)) {
          m.set(hh, {
            bufferPrevio: false,
            sesionReal: false,
            bufferPost: false,

            // capacidad (para topables)
            topables: 0,
            capacidadMax: 2,

            // exclusividad (si hay privada o prebuffer)
            exclusiva: false,
          });
        }

        const info = m.get(hh);

        const inPrev = pre > 0 && cur >= preStart && cur < start;
        const inReal = cur >= start && cur < end;

        if (inPrev) {
          info.bufferPrevio = true;
          info.exclusiva = true;
        }

        if (inReal) {
          info.sesionReal = true;

          if (esGrupal) {
            info.topables += 1;
            info.capacidadMax = Math.max(info.capacidadMax ?? 2, cap);
          } else {
            info.exclusiva = true;
          }
        }

        m.set(hh, info);
      }
    });

    return m;
  }, [reservations, date]);


  /* =============================
     4) Detectar si el slot está en break
  ============================== */
  const isBreak = (hhmm) => {
    const s = toDate(date, hhmm);
    const e = addMin(s, 30);
    return breaks.some((b) =>
      overlaps(s, e, toDate(date, b.start), toDate(date, b.end))
    );
  };

  /* =============================
     RENDER
  ============================== */
return (
  <>
    {/* ========================== */}
    {/* POPUP SELECTOR DE ACCIÓN  */}
    {/* ========================== */}
    {multiChoice && (
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-lg p-4 w-80 space-y-3">
          <h4 className="font-semibold text-zinc-800 text-center">
            Selecciona una acción
          </h4>

          {/* Reservas existentes */}
          {multiChoice.reservas.map((r) => (
            <button
              key={r.id}
              onClick={() => {
                onSlotClick?.({
                  status: "reserved",
                  reservaId: r.id,
                  dateISO: multiChoice.dateISO,
                  start: multiChoice.hour,
                  end: multiChoice.endHHmm,
                  isBreak: multiChoice.isBreak,
                  reservasEnBloque: multiChoice.reservas,
                });
                setMultiChoice(null);
              }}
              className="w-full text-left px-3 py-2 rounded-lg border border-zinc-200 hover:bg-zinc-50"
            >
              <div className="font-medium">
                {r.nombre || r.email || "Paciente"}
              </div>
              <div className="text-xs text-zinc-500">
                {r.servicio_categoria}
                {r.servicio_variante ? ` — ${r.servicio_variante}` : ""}
              </div>
            </button>
          ))}

          {/* Crear reserva manual */}
          <button
            onClick={() => {
              onSlotClick?.({
                status: "manual",
                dateISO: multiChoice.dateISO,
                start: multiChoice.hour,
                end: multiChoice.endHHmm,
                reservasEnBloque: multiChoice.reservas,
                isBreak: multiChoice.isBreak,
              });
              setMultiChoice(null);
            }}
            className="w-full text-left px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
          >
            ➕ Crear nueva reserva manual
          </button>

          <button
            onClick={() => setMultiChoice(null)}
            className="w-full py-2 text-sm text-zinc-600 hover:text-zinc-900 mt-2"
          >
            Cancelar
          </button>
        </div>
      </div>
    )}

    {/* ========================== */}
    {/* AGENDA DEL DÍA            */}
    {/* ========================== */}
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-zinc-800">Agenda del día</h3>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={abrirReservaManualLibre}
          className="text-xs px-2 py-1 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
          title="Crear una reserva manual a cualquier hora"
        >
          + Hora manual
        </button>

        <div className="text-sm text-zinc-500">{date}</div>
      </div>
    </div>


      <div className="space-y-2">
{/* ========================== */}
{/* TIMELINE estilo Calendar   */}
{/* ========================== */}
{(() => {
  const PX_PER_MIN = 2; // ajusta densidad visual (1.5 / 2 / 3)

  const VIEW_START = "00:00";
  const VIEW_END = "23:59";

  const dayStart = toDate(date, VIEW_START);

  let dayEnd = toDate(date, VIEW_END);
  // 00:00 es menor que 07:30, así que significa medianoche del día siguiente
  if (dayEnd <= dayStart) dayEnd = addMin(dayEnd, 24 * 60);
  const totalMin = Math.max(1, Math.round((dayEnd - dayStart) / 60000));
  const heightPx = totalMin * PX_PER_MIN;

  const clamp = (d) => Math.max(0, Math.min(totalMin, Math.round((d - dayStart) / 60000)));

  const snapTo30 = (d) => {
    const min = clamp(d);
    const snapped = Math.min(totalMin, Math.round(min / 30) * 30);
    return addMin(dayStart, snapped);
  };

  const yToSnappedHH = (clientY, rectTop) => {
  const y = clientY - rectTop;
  const minFromStart = Math.round(y / PX_PER_MIN);
  const at = addMin(dayStart, minFromStart);
  const snapped = snapTo30(at);
  return toHHmm(snapped);
};


  // Reservas con ventanas (bloque total incluye buffers)
  const resBlocks = (reservations || [])
    .filter((r) => r?.hora)
    .map((r) => {
      const w = getWindows(date, r);

      // clip al horario del día
      const bs = new Date(Math.max(w.blockStart.getTime(), dayStart.getTime()));
      const be = new Date(Math.min(w.blockEnd.getTime(), dayEnd.getTime()));
      if (bs >= be) return null;

      return { type: "reserva", r, w, bs, be };
    })
    .filter(Boolean)
    .sort((a, b) => a.bs - b.bs);

  // ===== Layout estilo Google Calendar (columnas por solape) =====
const placedRes = (() => {
  // usamos SESIÓN REAL para detectar solapes
  const items = resBlocks
    .map((x) => {
      const sesionIni = x.w.start;
      const sesionFin = x.w.end;
      return {
        ...x,
        sesionIni,
        sesionFin,
        sMin: clamp(sesionIni),
        eMin: clamp(sesionFin),
      };
    })
    .sort((a, b) => a.sMin - b.sMin || a.eMin - b.eMin);

  let active = [];
  let group = [];
  let groupMax = 0;
  const out = [];

  const finalizeGroup = () => {
    group.forEach((ev) => out.push({ ...ev, cols: groupMax }));
    group = [];
    groupMax = 0;
  };

  for (const ev of items) {
    // sacar eventos que ya terminaron antes del inicio de este
    active = active.filter((a) => a.eMin > ev.sMin);

    // si no queda nada activo, cerramos el grupo anterior
    if (active.length === 0 && group.length > 0) finalizeGroup();

    // asignar primera columna libre
    const used = new Set(active.map((a) => a.col));
    let col = 0;
    while (used.has(col)) col++;

    const placed = { ...ev, col };
    active.push(placed);
    group.push(placed);

    groupMax = Math.max(groupMax, active.length);
  }

  if (group.length > 0) finalizeGroup();

  return out;
})();


  // Breaks
  const breakBlocks = (breaks || [])
    .map((b) => {
      const s0 = toDate(date, b.start);
      const e0 = toDate(date, b.end);

      const s = new Date(Math.max(s0.getTime(), dayStart.getTime()));
      const e = new Date(Math.min(e0.getTime(), dayEnd.getTime()));
      if (s >= e) return null;

      return { type: "break", b, s, e };
    })
    .filter(Boolean)
    .sort((a, b) => a.s - b.s);

  // líneas de hora (cada 30 min)
  const hourLines = [];
  for (let t = new Date(dayStart); t <= dayEnd; t = addMin(t, 30)) {
    hourLines.push(new Date(t));
  }

  const isInsideHorarioBase = (hh) => {
  const t = hhmmToMin(hh);
  const s = hhmmToMin(horarioBase.start);
  const e = hhmmToMin(horarioBase.end);
  // inclusive para mantener tu comportamiento actual (tu buildSlots30 incluye el end)
  return t >= s && t <= e;
};


  const openMultiChoiceAt = (atDate) => {
    const at = atDate;

    // Reservas cuya SESIÓN real cubre el punto clickeado
    const hits = (reservations || []).filter((r) => {
      if (!r?.hora) return false;
      const w = getWindows(date, r);
      return at >= w.start && at < w.end;
    });

    if (hits.length > 0) {
      setMultiChoice({
        dateISO: date,
        hour: toHHmm(at),
        reservas: hits,
        endHHmm: toHHmm(addMin(at, 30)),
        isBreak: isBreak(toHHmm(at)),
      });
      return true;
    }
    return false;
  };

const handleTimelineClick = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();

  const hh = yToSnappedHH(e.clientY, rect.top);

  // 👇 Date correcto aunque sea "00:00" (día siguiente)
  const snapped = toDateInView(date, hh, VIEW_START);

  // si clickeaste encima de una reserva, abre multiChoice (esto sí puede funcionar siempre)
  if (openMultiChoiceAt(snapped)) return;


  const esB = isBreak(hh);

  onSlotClick?.({
    status: "manual",
    dateISO: date,
    start: hh,
    end: toHHmm(addMin(snapped, 30)),
    reservasEnBloque: [],
    isBreak: esB,
  });
};




  return (
    <div className="mt-2">
      <div className="flex gap-3">
        {/* Columna labels */}
        <div className="w-14 text-xs text-zinc-400 select-none">
          {hourLines.map((t) => {
            const hh = toHHmm(t);
            return (
              <button
                key={t.toISOString()}
                type="button"
                style={{ height: 30 * PX_PER_MIN }}
                className={`w-full flex items-start justify-end pr-1 rounded-md ${
                  isInsideHorarioBase(hh)
                    ? "hover:text-zinc-700 hover:bg-zinc-50"
                    : "text-zinc-400 hover:text-zinc-700 hover:bg-emerald-50"
                }`}
                onClick={(ev) => {
                  ev.stopPropagation();

                  const snapped = toDateInView(date, hh, VIEW_START);

                  // si hay una reserva en esa hora, abre selector
                  if (openMultiChoiceAt(snapped)) return;

                  const esB = isBreak(hh);
                  onSlotClick?.({
                    status: "manual",
                    dateISO: date,
                    start: hh,
                    end: toHHmm(addMin(snapped, 30)),
                    reservasEnBloque: [],
                    isBreak: esB,
                  });
                }}
              >
                {hh}
              </button>
            );
          })}
        </div>

        {/* Timeline */}
          <div
            className="relative flex-1 rounded-xl border border-zinc-200 bg-white overflow-hidden touch-none"
            style={{ height: heightPx }}
            onClick={handleTimelineClick}
          >
          {/* Grid (cada 30 min) */}
          {Array.from({ length: Math.ceil(totalMin / 30) + 1 }).map((_, i) => (
            <div
              key={i}
              className="absolute left-0 right-0 border-t border-zinc-100"
              style={{ top: i * 30 * PX_PER_MIN }}
            />
          ))}

          {/* Bandas por slot (buffers / cupos) */}
          {Array.from(slotInfo.keys()).map((hh) => {
            const s = toDate(date, hh);
            if (s < dayStart || s > dayEnd) return null;

            const info = slotInfo.get(hh);
            if (!info) return null;

            // top/h por bloque de 30m
            const top = clamp(s) * PX_PER_MIN;
            const h = 30 * PX_PER_MIN;

            const showPrev = info.bufferPrevio;
            if (!showPrev) return null;

            return (
              <div
                key={`band-${hh}`}
                className="absolute left-0 w-1 rounded-r pointer-events-none bg-amber-300"
                style={{ top, height: h }}
              />
            );

          })}


          {/* Break blocks */}
          {breakBlocks.map((x, idx) => {
            const top = clamp(x.s) * PX_PER_MIN;
            const h = Math.max(8, (clamp(x.e) - clamp(x.s)) * PX_PER_MIN);
            return (
              <div
                key={`br-${idx}`}
                className="absolute left-2 right-2 rounded-lg bg-purple-50 border border-purple-200 text-purple-800 text-xs px-2 py-1"
                style={{ top, height: h }}
              >
                Break {toHHmm(x.s)}–{toHHmm(x.e)}
              </div>
            );
          })}

          {/* Reserva blocks (columnas tipo Calendar) */}
          {placedRes.map((x) => {
            const sesionIni = x.sesionIni;
            const sesionFin = x.sesionFin;

            const top = clamp(sesionIni) * PX_PER_MIN;
            const h = Math.max(18, (clamp(sesionFin) - clamp(sesionIni)) * PX_PER_MIN);

            const colsRaw = Math.max(1, x.cols || 1);
            const colRaw = Math.max(0, x.col || 0);

            const cols = colsRaw;
            const col = colRaw;

            const gap = isMobile ? 6 : 8;
            const pad = isMobile ? 6 : 8;

            const widthPct = 100 / cols;
            const leftPct = col * widthPct;

            const left = `calc(${leftPct}% + ${col * gap}px + ${pad}px)`;
            const width = `calc(${widthPct}% - ${gap}px - ${pad * 2 / cols}px)`;

            return (
              <div
                key={x.r.id || `${x.r.hora}-${x.r.email}`}
                className={`absolute rounded-xl border border-zinc-200 bg-zinc-50 cursor-pointer ${
                  isMobile ? "px-2 py-2" : "px-3 py-2"
                }`}
                style={{ top, height: h, left, width }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  openMultiChoiceAt(sesionIni);
                }}
              >
                <div className="flex items-center gap-2">
                  <EstadoPill estado={x.r.estadoVisual || x.r.estado} />
                  <div className="font-semibold text-zinc-800 text-sm truncate">
                    {x.r.nombre || x.r.email}
                  </div>
                </div>

                <div className={`${isMobile ? "text-[11px]" : "text-[12px]"} text-zinc-500 mt-1 truncate`}>
                  {x.r.servicio_categoria}
                  {x.r.servicio_variante ? ` — ${x.r.servicio_variante}` : ""}
                </div>

                <div className="mt-2 flex items-baseline gap-3">
                  <div className={`${isMobile ? "text-[14px]" : "text-[15px]"} font-semibold text-zinc-700`}>
                    {toHHmm(sesionIni)}–{toHHmm(sesionFin)}
                  </div>

                  {vistaTecnica && (
                    <div className="text-[11px] text-zinc-400">
                      Bloqueado: {toHHmm(x.bs)}–{toHHmm(x.be)}
                    </div>
                  )}
                </div>

                {(() => {
                  const regla = getReglaFor(x.r) || {};
                  const esGrupal = isGrupalReserva(x.r);

                  const startKey = toHHmm(x.w.start);
                  const occ = slotInfo.get(startKey);
                  const cupo =
                    esGrupal && occ ? `${occ.topables}/${occ.capacidadMax ?? 2}` : null;

                  return (
                    <div className="mt-2 text-[11px] text-zinc-500">
                      <b>{esGrupal ? `Grupal${cupo ? ` ${cupo}` : ""}` : "Privada"}</b>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
})()}

      
    </div>
  </>
);

}
